'use client'

import {
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Stack,
  Text,
} from '@chakra-ui/react'
import { forwardRef } from 'react'
import { FaChartBar } from 'react-icons/fa'
import { FiExternalLink } from 'react-icons/fi'
import type {
  IRCompanyConfig,
  IRThemeConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'

interface MarketDataSectionProps {
  useContextFunction: () => InvestorRelationsConfigContextType
}

export const MarketDataSection = forwardRef<
  HTMLElement,
  MarketDataSectionProps
>(function MarketDataSection({ useContextFunction }, ref) {
  const { config, loading, error } =
    useContextFunction() as InvestorRelationsConfigContextType

  const companyConfig = ((config?.companyConfig as IRCompanyConfig) ||
    {}) as IRCompanyConfig
  const companyName = companyConfig.companyName
  const stockSymbol = companyConfig.companyStockTicker
  const sharePriceLink = companyConfig.sharePriceLink
  const stockExchange = companyConfig.companyStockExchange

  const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
    {}) as IRThemeConfig
  const primaryColor = themeConfig.primaryColor
  const textColor = themeConfig.textColor
  const fontFamily = themeConfig.fontStyle

  const handleViewMarketData = () => {
    window.open(sharePriceLink, '_blank')
  }

  return (
    <Box
      ref={ref}
      as="section"
      id="market-data"
      bg="white"
      minH="calc(100vh - 50px)"
      display="flex"
      alignItems="center"
      py={{ base: 10, md: 16 }}
    >
      <Container maxW="6xl">
        <Stack gap={{ base: 6, md: 8 }} align="center" textAlign="center">
          <Heading
            as="h2"
            size={{ base: 'lg', md: '5xl' }}
            color={primaryColor}
            fontWeight="bold"
            mb={3}
            textAlign="center"
            fontFamily={fontFamily}
          >
            Market Data
          </Heading>

          <Stack gap={4} maxW="2xl">
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color={primaryColor}
              fontFamily={fontFamily}
            >
              📈{' '}
              <Text as="span" fontWeight="bold" fontFamily={fontFamily}>
                {companyName}
              </Text>{' '}
              trades on the {companyConfig.companyStockExchange} under the
              symbol{' '}
              <Text as="span" fontWeight="bold" fontFamily={fontFamily}>
                {stockSymbol}
              </Text>
              .
            </Text>

            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              color={textColor}
              fontFamily={fontFamily}
            >
              Access live market data, historical performance, and comprehensive
              trading information for the company on {stockExchange}.
            </Text>
          </Stack>

          <Button
            onClick={handleViewMarketData}
            color="white"
            bg={primaryColor}
            variant="solid"
            size="lg"
            px={8}
            py={6}
            borderRadius="md"
            fontWeight="bold"
          >
            <HStack gap={2}>
              <FaChartBar />
              <Text fontFamily={fontFamily}>View Live Market Data</Text>
              <FiExternalLink />
            </HStack>
          </Button>
        </Stack>
      </Container>
    </Box>
  )
})
